import type { JSX } from 'react';
import type { BrowserAgentSitesViewSite } from '@kirin/api-client/contracts/shadow-ai';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { useBrowserAgentSiteUsers } from '@/hooks/api/useShadowAiTools';
import { ToolUsersTable } from '../OrgShadowAITab/ToolUsersTable';

/**
 * The users behind one Browser Agent Sites row (KIR-4704 review #15), opened
 * from the row's or the expanded row's Users count. Reuses the Shadow AI tool
 * users table: one row per user endpoint active in the last 30 days.
 */
export function BrowserAgentSiteUsersModal({
  organizationId,
  site,
  onClose,
}: {
  organizationId: string | undefined;
  site: BrowserAgentSitesViewSite | null;
  onClose: () => void;
}): JSX.Element {
  const { data, isLoading, isError } = useBrowserAgentSiteUsers(
    organizationId,
    site ? { host: site.siteHost, agentType: site.agentType } : null,
    site !== null
  );
  const users = data?.users ?? [];

  return (
    <Dialog
      open={site !== null}
      onOpenChange={open => {
        if (!open) onClose();
      }}
    >
      {site && (
        <DialogContent className="sm:max-w-3xl">
          <DialogHeader>
            <DialogTitle className="truncate pr-6">
              Users of <span className="font-mono">{site.siteHost}</span>
            </DialogTitle>
            <DialogDescription>
              {site.agentName} · {site.reachedVia} · active in the last 30 days
            </DialogDescription>
          </DialogHeader>
          <div className="max-h-[60vh] overflow-y-auto">
            {isError ? (
              <p className="text-sm text-destructive">
                Failed to load users for this site. Please try again.
              </p>
            ) : isLoading ? (
              <p className="text-sm text-muted-foreground">Loading users...</p>
            ) : users.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No users in the last 30 days.
              </p>
            ) : (
              // Rows are per endpoint; the default heading counts people and
              // endpoints distinctly, so it agrees with the row's Users count.
              <ToolUsersTable users={users} />
            )}
          </div>
        </DialogContent>
      )}
    </Dialog>
  );
}
